import { TenantBranding } from './types';
import { defaultBranding } from './defaultBranding';

/**
 * Resolve tenant branding BEFORE the React tree is rendered [STD-FE]:
 * call this in `main.tsx` and only `createRoot(...).render(...)` after it
 * settles. While it is pending, the neutral splash markup in `index.html`
 * is visible — the tenant theme is therefore applied on the very first
 * React paint and there is NO branding flash.
 *
 * On failure (Control Plane down, unknown tenant) it falls back to the
 * neutral default branding instead of blocking the app.
 */
export async function resolveBrandingBeforePaint(
  loader: () => Promise<TenantBranding>,
  fallback: TenantBranding = defaultBranding
): Promise<TenantBranding> {
  try {
    return await loader();
  } catch (err) {
    console.warn('[zercode/theme] tenant branding could not be resolved, using neutral fallback', err);
    return fallback;
  }
}

/**
 * Berry seam — `useConfig()` input. Berry's layout components read
 * `fontFamily` / `borderRadius` from its ConfigContext; this maps the
 * Control Plane branding response onto those config fields so Berry's
 * config is fed by tenant branding, not user prefs / localStorage [STD-FE].
 */
export function brandingToBerryConfig(branding: TenantBranding): { fontFamily?: string; borderRadius?: number } {
  return {
    ...(branding.typography.fontFamily ? { fontFamily: branding.typography.fontFamily } : {}),
    ...(branding.borderRadius !== undefined ? { borderRadius: branding.borderRadius } : {})
  };
}

const FONT_LINK_ATTR = 'data-zercode-branding-font';

/**
 * Inject the tenant webfont stylesheet as `<link rel="stylesheet">`.
 * Call BEFORE `createRoot(...).render(...)` so the font is requested before the
 * first React paint (minimizes font flash). Idempotent: re-calling updates the
 * existing link (used by the live branding refresh after a settings save).
 * If the stylesheet fails to load, the CSS font-family stack falls back to the
 * system font — a broken font URL must never block or break the app.
 */
export function injectBrandingFontLink(fontUrl: string | undefined | null): void {
  const existing = document.head.querySelector<HTMLLinkElement>(`link[${FONT_LINK_ATTR}]`);

  if (!fontUrl) {
    existing?.remove();
    return;
  }
  if (existing) {
    if (existing.href !== fontUrl) existing.href = fontUrl;
    return;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = fontUrl;
  link.setAttribute(FONT_LINK_ATTR, 'true');
  link.onerror = () => {
    console.warn(`[zercode/theme] tenant font stylesheet could not be loaded (${fontUrl}), system font fallback applies`);
  };
  document.head.appendChild(link);
}

/**
 * Apply document-level branding: title, favicon, tenant webfont link and the
 * initial color scheme attribute. Call once BEFORE first render (main.tsx) and
 * again after a live branding update.
 */
export function applyBrandingToDocument(branding: TenantBranding): void {
  /**
   * ⚠ `appTitle` FIRST, AND IT IS WHY THE TITLES WERE STUCK. This read `tenantName` alone — a field the
   * branding wire does not actually carry, so the guard never fired and every app kept the title compiled
   * into its own index.html with nothing reporting a problem. Those compiled titles used to name the
   * PLATFORM ("ZerEdu", "ZerEdu Student", …) and are now the app's ROLE ("Admin", "Student", …) —
   * BRAND-LEAK: the fallback is reached by every tenant who has not set `appTitle`, so it is the common
   * case, not an edge one. `appTitle`
   * is a tenant setting that travels in `extras`, so it needs no wire or DTO change; `tenantName` stays
   * as the fallback for the day the server does send it.
   *
   * ⚠ ONE STRING FOR EVERY SPA, BY REQUEST. No per-app suffix is appended: the tenant types what they
   * want in a tab and that is what appears, in admin, student, teacher, super-admin and marketing alike.
   * Leaving each app's index.html title in place as the pre-branding default is what keeps the tabs
   * distinguishable for a tenant who sets nothing.
   */
  const title = branding.appTitle ?? branding.tenantName;
  if (title) document.title = title;
  if (branding.logo.favicon) {
    let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.logo.favicon;
  }

  // tenant webfont requested before render — minimizes font flash [STD-FE]
  injectBrandingFontLink(branding.typography.fontUrl);

  // pre-set MUI's colorSchemeSelector attribute so the FIRST paint already uses
  // the tenant's default scheme (an explicit user choice persisted by MUI under
  // `zercode-theme-mode` still wins once the ThemeProvider hydrates).
  if (branding.defaultColorScheme && !document.documentElement.getAttribute('data-color-scheme')) {
    document.documentElement.setAttribute('data-color-scheme', branding.defaultColorScheme);
  }
}
